/* ==========================================================================
   Collection Tabs — Carrusel
   - Tabs accesibles (roving tabindex + flechas del teclado)
   - Scroll por "página" con las flechas, estado disabled sin layout thrash
   - Reveal de la sección al entrar en pantalla
   - Compatible con el editor de temas (section:load / block:select)
   ========================================================================== */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SMOOTH = REDUCE ? 'auto' : 'smooth';

  function CollectionTabs(root) {
    if (!root || root.ctInstance) return;
    root.ctInstance = this;

    this.root = root;
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('.collection-tabs__nav-btn'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('.collection-tabs__content'));
    this.observers = [];

    this.bindTabs();
    this.panels.forEach(this.setupSlider, this);
    this.reveal();
  }

  /* ---------------- Tabs ---------------- */
  CollectionTabs.prototype.bindTabs = function () {
    var self = this;
    if (!this.tabs.length) return;

    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { self.select(i); });
    });

    var nav = this.root.querySelector('.collection-tabs__nav');
    if (!nav) return;

    nav.addEventListener('keydown', function (e) {
      var current = self.tabs.indexOf(document.activeElement);
      if (current === -1) return;

      var next = null;
      if (e.key === 'ArrowRight') next = (current + 1) % self.tabs.length;
      else if (e.key === 'ArrowLeft') next = (current - 1 + self.tabs.length) % self.tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = self.tabs.length - 1;
      if (next === null) return;

      e.preventDefault();
      self.select(next);
      self.tabs[next].focus();
    });
  };

  CollectionTabs.prototype.select = function (index) {
    var self = this;
    if (!this.tabs[index] || this.tabs[index].classList.contains('active')) return;

    this.tabs.forEach(function (tab, i) {
      var on = i === index;
      tab.classList.toggle('active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
    });

    this.panels.forEach(function (panel, i) {
      panel.classList.toggle('active', i === index);
    });

    // El panel recién se puede medir cuando ya está visible
    requestAnimationFrame(function () {
      var panel = self.panels[index];
      if (panel && panel._ctUpdate) panel._ctUpdate();
    });
  };

  /* ---------------- Slider ---------------- */
  CollectionTabs.prototype.setupSlider = function (panel) {
    var track = panel.querySelector('.slider-track');
    var prev = panel.querySelector('.slider-arrow--prev');
    var next = panel.querySelector('.slider-arrow--next');
    if (!track) return;

    var ticking = false;

    function step() {
      var item = track.querySelector('.slider-item');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var itemWidth = item ? item.getBoundingClientRect().width + gap : 0;
      if (!itemWidth) return track.clientWidth * 0.9;

      var perView = Math.max(1, Math.floor((track.clientWidth + gap) / itemWidth));
      return itemWidth * perView;
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var overflows = max > 2;
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft >= max - 2;

      if (prev) {
        prev.classList.toggle('disabled', !overflows || atStart);
        prev.setAttribute('tabindex', !overflows || atStart ? '-1' : '0');
      }
      if (next) {
        next.classList.toggle('disabled', !overflows || atEnd);
        next.setAttribute('tabindex', !overflows || atEnd ? '-1' : '0');
      }
    }

    panel._ctUpdate = update;

    // Un solo update por frame: el listener de scroll no puede bloquear el hilo
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }, { passive: true });

    if (prev) prev.addEventListener('click', function () {
      track.scrollBy({ left: -step(), behavior: SMOOTH });
    });

    if (next) next.addEventListener('click', function () {
      track.scrollBy({ left: step(), behavior: SMOOTH });
    });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { update(); });
      ro.observe(track);
      this.observers.push(ro);
    } else {
      window.addEventListener('resize', update);
    }

    update();
  };

  /* ---------------- Reveal ---------------- */
  CollectionTabs.prototype.reveal = function () {
    var root = this.root;
    if (!root.classList.contains('ct-reveal')) return;

    if (REDUCE || !('IntersectionObserver' in window)) {
      root.classList.add('is-visible');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });

    io.observe(root);
    this.observers.push(io);
  };

  CollectionTabs.prototype.destroy = function () {
    this.observers.forEach(function (o) { o.disconnect(); });
    this.observers = [];
    this.root.ctInstance = null;
  };

  /* ---------------- Init ---------------- */
  function init(scope) {
    (scope || document).querySelectorAll('.collection-tabs').forEach(function (el) {
      new CollectionTabs(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Editor de temas */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('.collection-tabs');
    if (el && el.ctInstance) el.ctInstance.destroy();
  });

  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest('.collection-tabs');
    if (!root || !root.ctInstance) return;
    var index = parseInt(e.target.getAttribute('data-tab-index'), 10);
    if (!isNaN(index)) root.ctInstance.select(index);
  });
})();
